import {
  CREDIT_TOP_UP_MAX_USD,
  CREDIT_TOP_UP_MIN_USD,
  CREDITS_PER_USD,
  MINUTE_TOP_UP_MAX_MINUTES,
  MINUTE_TOP_UP_MIN_MINUTES,
  MINUTE_TOP_UP_MINUTES_PER_USD,
} from '@highlight/common'
import { planTierLabel, type SelectableV2Plan } from './plan-select-cards'

/** A chosen top-up: whole dollars, and what those dollars buy. */
export interface TopUpSelection {
  dollars: number
  units: number
}

/**
 * Everything that differs between the two top-ups. The flow around them —
 * slider, card-on-file CTA, upgrade alternative, purchase latch, analytics —
 * is identical, so it lives once and reads the rest from here.
 *
 * Deliberately free of data-layer imports: the paywall body reads this, and it
 * has to stay renderable from a story.
 */
export interface TopUpKindSpec {
  kind: 'credits' | 'hours'
  /**
   * Amplitude names, frozen at what they were when the copy still said
   * minutes. Meeting time is sold in hours and charged in minutes, so these
   * part ways from `kind` rather than splitting the funnel at the rename.
   */
  analytics: {
    /** Event action for a purchase click. */
    action: string
    /** Property name for the quantity bought, and the unit it is counted in. */
    unit: string
    /** Feature the Paywall 'shown' event reports. */
    feature: string
  }
  title: (plan: SelectableV2Plan) => string
  /** The line under the title once the allowance this top-up refills is spent. */
  limitMessage: (plan: SelectableV2Plan) => string
  /** The same line while there is still headroom, as when settings opens the top-up by choice. */
  headroomMessage: (plan: SelectableV2Plan) => string
  ctaLabel: string
  minDollars: number
  maxDollars: number
  /** Where the slider rests. Meeting hours anchor per tier; credits are flat. */
  defaultDollars: (plan: SelectableV2Plan) => number
  /** What the backend is asked to grant: credits for credits, minutes for meeting hours. */
  unitsFor: (dollars: number) => number
  /** The readout beside the amount, e.g. "for 1,000 credits". */
  amountLabel: (dollars: number) => string
}

// Rate and bounds come from billing-shared so the UI price and the backend
// charge can't drift.
const MIN_HOUR_DOLLARS = MINUTE_TOP_UP_MIN_MINUTES / MINUTE_TOP_UP_MINUTES_PER_USD
const MAX_HOUR_DOLLARS = MINUTE_TOP_UP_MAX_MINUTES / MINUTE_TOP_UP_MINUTES_PER_USD

/** Where each tier's meeting-hours slider rests, from the design's anchors. */
const DEFAULT_HOUR_DOLLARS: Record<SelectableV2Plan, number> = {
  lite: 10,
  pro2: 25,
  max: 50,
}

// Always hours, never minutes: $1 buys an hour, and the $1 slider step keeps
// hours whole, so the readout doubles as the rate.
function formatHours(minutes: number): string {
  const hours = minutes / 60
  return `${hours} ${hours === 1 ? 'hour' : 'hours'}`
}

export const CREDITS_TOP_UP: TopUpKindSpec = {
  kind: 'credits',
  analytics: { action: 'add credits clicked', unit: 'credits', feature: 'Credits' },
  title: (plan) => `Add ${planTierLabel(plan)} credits`,
  limitMessage: () => 'You’ve reached your plan limit. Add credits to keep working with Highlight.',
  headroomMessage: () => 'Stay ahead of your plan limit. Add credits anytime, and they never expire.',
  ctaLabel: 'Add credits',
  minDollars: CREDIT_TOP_UP_MIN_USD,
  maxDollars: CREDIT_TOP_UP_MAX_USD,
  defaultDollars: () => 10,
  unitsFor: (dollars) => dollars * CREDITS_PER_USD,
  amountLabel: (dollars) => `for ${(dollars * CREDITS_PER_USD).toLocaleString('en-US')} credits`,
}

export const MEETING_HOURS_TOP_UP: TopUpKindSpec = {
  kind: 'hours',
  analytics: { action: 'add minutes clicked', unit: 'minutes', feature: 'Audio Note Minutes' },
  title: () => 'Add meeting hours',
  limitMessage: (plan) =>
    `You’ve used all the meeting hours on your ${planTierLabel(plan)} plan. Add more to stay on top of all your conversations.`,
  headroomMessage: (plan) =>
    `Add meeting hours to your ${planTierLabel(plan)} plan anytime. They never expire, so you stay on top of every conversation.`,
  ctaLabel: 'Add hours',
  minDollars: MIN_HOUR_DOLLARS,
  maxDollars: MAX_HOUR_DOLLARS,
  defaultDollars: (plan) => DEFAULT_HOUR_DOLLARS[plan],
  unitsFor: (dollars) => dollars * MINUTE_TOP_UP_MINUTES_PER_USD,
  amountLabel: (dollars) => `for ${formatHours(dollars * MINUTE_TOP_UP_MINUTES_PER_USD)}`,
}

export type TopUpKind = TopUpKindSpec['kind']

export const TOP_UP_SPECS: Record<TopUpKind, TopUpKindSpec> = {
  credits: CREDITS_TOP_UP,
  hours: MEETING_HOURS_TOP_UP,
}
